/**
 * Shared map-rendering primitives used by both the Live Map (FleetMap) and
 * the History playback map (MapPage).
 *
 * Extracting these ensures the two surfaces share one basemap/route/stop
 * look-and-feel and one set of theme-aware tile URLs, instead of each
 * rolling its own.
 */
import { Fragment, useEffect, useMemo, useState } from "react";
import { CircleMarker, Polyline, Popup, TileLayer, Tooltip, useMap } from "react-leaflet";
import type { LatLngExpression } from "leaflet";
import type { RouteShape, RouteStop } from "../api/types";
import { basemapLayers, basemapKey } from "./basemap";

export const STOP_MIN_ZOOM = 12;
export const STOP_LABEL_ZOOM = 14;

/**
 * Theme-aware basemap. Provider and key come from `basemap.ts`, which falls
 * back to keyless OpenStreetMap tiles — the hardcoded CARTO URLs that used to
 * live here carried no API key, and CARTO stamps "API KEY REQUIRED" across
 * keyless raster tiles, so every map in the console rendered the watermark.
 */
export function MapTileLayers({ theme }: { theme: "dark" | "light" }) {
  const tiles = basemapLayers(theme);
  const key = basemapKey(theme);
  const map = useMap();
  // Desaturate a full-colour basemap so data overlays read against it. Applied
  // to the container rather than the tile layer so it survives a layer swap.
  useEffect(() => {
    const el = map.getContainer();
    el.classList.toggle("occ-basemap-vivid", !tiles.muted);
    return () => el.classList.remove("occ-basemap-vivid");
  }, [map, tiles.muted]);
  return (
    <>
      <TileLayer
        key={key}
        attribution={tiles.base.attribution}
        url={tiles.base.url}
        subdomains={tiles.base.subdomains ?? ""}
      />
      {tiles.labels ? (
        <TileLayer
          key={key + "-labels"}
          attribution=""
          url={tiles.labels.url}
          subdomains={tiles.labels.subdomains ?? ""}
          pane="shadowPane"
        />
      ) : null}
    </>
  );
}

/** Invalidate Leaflet size on container resize. */
export function InvalidateOnResize() {
  const map = useMap();
  useEffect(() => {
    const c = map.getContainer();
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(c);
    requestAnimationFrame(() => map.invalidateSize());
    return () => ro.disconnect();
  }, [map]);
  return null;
}

/** Tracks the map's zoom so layers can thin out when zoomed away. */
export function useMapZoom(): number {
  const map = useMap();
  const [zoom, setZoom] = useState(() => map.getZoom());
  useEffect(() => {
    const onZoom = () => setZoom(map.getZoom());
    map.on("zoomend", onZoom);
    return () => { map.off("zoomend", onZoom); };
  }, [map]);
  return zoom;
}

/**
 * Stations along the routes.
 *
 * Zoom-gated: dots appear once the view is close enough for them to mean
 * something, and names appear only when stations are well-separated —
 * standard transit-map behaviour that lets the layer stay on permanently.
 */
export function RouteStopsOverlay({ stops, theme }: { stops: RouteStop[]; theme: "light" | "dark" }) {
  const zoom = useMapZoom();
  if (zoom < STOP_MIN_ZOOM) return null;
  const showLabels = zoom >= STOP_LABEL_ZOOM;

  return (
    <>
      {stops.map((st) => (
        <CircleMarker
          key={st.stop_id}
          center={[st.latitude, st.longitude]}
          radius={showLabels ? 6 : 4}
          pathOptions={{
            color: theme === "light" ? "#0f172a" : "#ffffff",
            weight: 2,
            fillColor: theme === "light" ? "#ffffff" : "#0f172a",
            fillOpacity: 1,
          }}
        >
          {showLabels ? (
            <Tooltip direction="top" offset={[0, -8]} opacity={1} permanent className="occ-stop-label">
              {st.name}
            </Tooltip>
          ) : null}
          <Popup>
            <div className="popup">
              <div><strong>{st.name}</strong></div>
              {st.code ? <div className="secondary-text">Stop {st.code}</div> : null}
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </>
  );
}

/**
 * The route network drawn beneath everything else.
 *
 * One line per route (lowest direction_id) — the real road path from GTFS
 * shapes. Deliberately quiet: casing contrasts with the basemap, colour
 * line sits inside, and the selected bus's route lifts out of the
 * background while the rest stay faint.
 */
export function RouteNetworkOverlay({
  shapes,
  highlightRouteId,
  theme,
}: {
  shapes: RouteShape[];
  highlightRouteId?: string;
  theme: "light" | "dark";
}) {
  const casing = theme === "light" ? "#ffffff" : "#0b1220";
  const casingOpacity = theme === "light" ? 0.9 : 0.35;
  const baseOpacity = theme === "light" ? 0.75 : 0.5;
  const oneLinePerRoute = useMemo(() => {
    const byRoute = new Map<string, RouteShape>();
    for (const s of shapes) {
      const existing = byRoute.get(s.route_id);
      if (!existing || s.direction_id < existing.direction_id) byRoute.set(s.route_id, s);
    }
    return [...byRoute.values()];
  }, [shapes]);

  return (
    <>
      {oneLinePerRoute.map((s) => {
        const active = highlightRouteId && s.route_id === highlightRouteId;
        const color = `#${s.color.replace(/^#/, "")}`;
        return (
          <Fragment key={s.route_id}>
            <Polyline
              positions={s.coordinates as LatLngExpression[]}
              pathOptions={{
                color: casing,
                weight: active ? 9 : 7,
                opacity: active ? Math.min(1, casingOpacity + 0.1) : casingOpacity,
                lineCap: "round",
                lineJoin: "round",
                interactive: false,
              }}
            />
            <Polyline
              positions={s.coordinates as LatLngExpression[]}
              pathOptions={{
                color,
                weight: active ? 5 : 3.5,
                opacity: active ? 0.95 : baseOpacity,
                lineCap: "round",
                lineJoin: "round",
              }}
            >
              <Popup>
                <div className="popup">
                  <div><strong>{s.route_name}</strong></div>
                  {s.route_long_name && s.route_long_name !== s.route_name ? (
                    <div className="secondary-text">{s.route_long_name}</div>
                  ) : null}
                </div>
              </Popup>
            </Polyline>
          </Fragment>
        );
      })}
    </>
  );
}
